import {
  AfterViewInit,
  Component,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { AuthService } from '../../../../services/API/auth.service/auth.service';
import { isPlatformBrowser, NgIf } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { OtpVerificationComponent } from '../otp-verification/otp-verification.component';
import { AuthContainerComponent } from '../../../../shared/components/auth-container/auth-container.component';
import flatpickr from 'flatpickr';
import { ICountry, NgxCountriesDropdownModule } from 'ngx-countries-dropdown';
import { AVAILABLE_COUNTRY_CODES } from '../../../../models/constant/constants';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { StorageService } from '../../../../services/state-management/storage.service/storage.service';
import { Country } from '../../../../models/interfaces/country.interface';

// declare let flatpickr: any
@Component({
  selector: 'app-sign-up',
  standalone: true,
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    OtpVerificationComponent,
    NgIf,
    AuthContainerComponent,
    RouterLink,
    NgxCountriesDropdownModule,
    NgSelectComponent,
    NgOptionComponent,
  ],
  providers: [AuthService],
})
export class SignUpComponent implements AfterViewInit {
  signUpForm: FormGroup;
  otpForm: FormGroup;
  isVerify: boolean = false;
  birthdayPlaceholder: string = 'MM-DD-YYYY';
  formattedDate: string = '';
  availableCountries = AVAILABLE_COUNTRY_CODES;
  uniqueAvailableCodes = this.availableCountries;
  countriesList: Array<Country> = [];
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private toastr: ToastrService,
    private authService: AuthService,
    private storageService: StorageService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.signUpForm = this.fb.group({
      name: ['', [Validators.required]],
      birthday: ['', [Validators.required, this.validateAge.bind(this)]],
      country: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.minLength(6), Validators.maxLength(15)]],
      terms: [false, Validators.requiredTrue],
      privacy: [false, Validators.requiredTrue],
    });

    this.otpForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });
    this.getCountries();
  }

  // date config
  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      flatpickr('#DOB', {
        enableTime: false,
        dateFormat: 'm-d-Y',
        allowInput: false,
        minDate: new Date(new Date().setFullYear(new Date().getFullYear() - 200)), // 200 years ago
        maxDate: new Date(), // Today's date
      });
      setTimeout(() => {
        const elements = document.querySelectorAll('.ipv_country-name');
        elements.forEach(el => {
          const match = el.textContent?.match(/^(.+?)\s\(.+\)$/);
          if (match) {
            el.textContent = match[1]; // just the English name
          }
        });
      }, 100)
    }

    ;
  }

  getCountries(): void {
    this.storageService.getCountries()
      .subscribe(res => {
        this.countriesList = res;
      })
  }

  // helper fun
  reverseDate(date: string): string {
    const [month, day, year] = date?.split('-'); // Split the date into components
    return `${year}-${month}-${day}`; // Rearrange and return
  }

  // Custom Validator for Age
  validateAge(control: any) {
    const selectedDate = new Date(control.value);
    const today = new Date();
    const minDate = new Date(today.setFullYear(today.getFullYear() - 200)); // 200 years ago

    if (selectedDate < minDate) {
      return { inValidMinAge: true }; // Error if outside range
    } else if (selectedDate > new Date()) {
      return { inValidMaxAge: true };
    }
    return null; // Valid
  }

  // Handles the "Next" button click for Sign Up
  formSubmitted = false;
  dialCodeOrPhoneMissing = false;

  isFormValid(): boolean {
    let isValid = true;

    if (this.signUpForm.invalid) {
      this.signUpForm.markAllAsTouched();
      this.toastr.error(
        'Please fill in all required fields correctly.',
        'Validation Error'
      );
      isValid = false;
    }

    if (!this.isMobileNumberValid()) {
      isValid = false;
    }

    return isValid;
  }

  isMobileNumberValid(): boolean {
    if ((this.signUpForm.value.phone && !this.selectedDialCode) || (!this.signUpForm.value.phone && this.selectedDialCode)) {
      this.dialCodeOrPhoneMissing = true;
      return false;
    }
    this.dialCodeOrPhoneMissing = false;
    return true;
  }

  onNext(): void {
    this.formSubmitted = true;
    if (!this.isFormValid()) return;

    const signUpPayload = {
      audience: true,
      dateOfBirth: this.reverseDate(this.signUpForm.value.birthday),
      email: this.signUpForm.value.email,
      fullName: this.signUpForm.value.name,
      country: this.signUpForm.value.country,
      mobileNumber:
        this.signUpForm.value.phone ?
          // Only add "+XX-" string if dial code is truthy 
          `${this.selectedDialCode ?
            `${this.selectedDialCode}-` :
            ""
          }${this.signUpForm.value.phone}` :
          undefined,
    };

    this.authService.signUp(signUpPayload).subscribe({
      next: (response) => {
        this.toastr.success(
          'Signup successful! Check your email for the OTP.',
          'Success'
        );
        // this.onSignUp()
        this.isVerify = true;
        // this.emailForOtp = this.signUpForm.value.email;
      },
      error: (err) => {
        this.toastr.error(
          err.error?.message || 'Signup failed. Please try again.',
          'Error'
        );
      },
    });
  }
  // Receive data from OTP verification child component
  receiveData(data: boolean) {
    this.isVerify = data; // Handle the data received from the child
  }
  // Checks if a field is valid
  isFieldValid(field: string): boolean {
    return (
      !!(this.signUpForm.get(field)?.valid ?? false) &&
      !!(this.signUpForm.get(field)?.touched ?? false)
    );
  }

  // Checks if a field is invalid
  isFieldInvalid(field: string): boolean {
    return (
      !!(this.signUpForm.get(field)?.invalid ?? false) &&
      !!(this.signUpForm.get(field)?.touched ?? false)
    );
  }
  // Navigate to Sign in
  onSignUp(): void {
    this.router.navigateByUrl('auth/login');
    // Add your navigation logic here
  }

  onPhoneNumberChange(event: any): void {
    let phoneValue = event.target.value;
    const digitsOnly = phoneValue.replace(/\D/g, "");
    this.signUpForm.get('phone')?.setValue(digitsOnly);

  }

  isDateValid(date: string): boolean {
    const regex = /^\d{4}-\d{2}-\d{2}$/; // YYYY-MM-DD format for type="date"
    if (!regex.test(date)) return false;

    const parsedDate = new Date(date);
    return !isNaN(parsedDate.getTime()); // Ensure the date is valid
  }



  dialCodeListConfig = {
    hideCode: true,
    hideName: true,
    hideFlag: true,
  };


  selectedDialCode: string | null = null;
  onDialCodeChange(country: ICountry) {
    this.selectedDialCode = country.dialling_code || "";
    this.isMobileNumberValid();
  }

  resetPhone() {
    this.selectedDialCode = "";
    this.signUpForm.get('phone')?.reset();
  }
}
